import { useEffect, useState } from "react";
import { useAuth } from "./use-auth";
import axios from "axios";
import { initializePaddle, Paddle } from "@paddle/paddle-js";

// Define premium user types
export interface PremiumStatus {
  isPremium: boolean;
  loading: boolean;
  error: string | null;
}

export function usePremium() {
  const { user } = useAuth();
  const [paddle, setPaddle] = useState<Paddle | null>(null);
  const [status, setStatus] = useState<PremiumStatus>({
    isPremium: false,
    loading: true,
    error: null,
  });

  // Initialize Paddle
  useEffect(() => {
    async function setupPaddle() {
      try {
        // Use the environment variables for Paddle configuration
        const paddleInstance = await initializePaddle({
          environment: "sandbox",
          token: import.meta.env.VITE_PADDLE_CLIENT_TOKEN || "",
        });

        if (paddleInstance) {
          setPaddle(paddleInstance);
        } else {
          throw new Error("Failed to initialize Paddle");
        }
      } catch (error) {
        console.error("Error initializing Paddle:", error);
        setStatus((prev) => ({
          ...prev,
          error: "Could not initialize payment system",
        }));
      }
    }

    setupPaddle();
  }, []);

  // Check if user is premium
  useEffect(() => {
    async function checkPremiumStatus() {
      if (!user) {
        setStatus({ isPremium: false, loading: false, error: null });
        return;
      }

      try {
        setStatus((prev) => ({ ...prev, loading: true }));

        // Get auth token before making the request
        const token = await user.getIdToken();
        if (!token) {
          console.error("Failed to get auth token");
          setStatus({
            isPremium: false,
            loading: false,
            error: "Authentication error",
          });
          return;
        }

        // Call backend to check subscription status with auth token
        const response = await axios.get("/api/premium/status", {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setStatus({
          isPremium: response.data?.isPremium || false,
          loading: false,
          error: null,
        });
      } catch (error) {
        // Don't show error in console for 401 - it's expected when not logged in
        if ((error as any)?.response?.status !== 401) {
          console.error("Error checking premium status:", error);
        }

        setStatus({
          isPremium: false,
          loading: false,
          error: null, // Don't show error to user as it's not critical
        });
      }
    }

    checkPremiumStatus();
  }, [user]);

  // Function to start the checkout process
  const startCheckout = async () => {
    if (!paddle) {
      console.error("Paddle is not initialized");
      throw new Error("Payment system not initialized");
    }

    if (!user) {
      console.error("User must be logged in to upgrade");
      throw new Error("User must be logged in to upgrade");
    }

    try {
      // Use type casting to bypass TypeScript type checking issues
      // This is necessary because Paddle's types don't fully match their API
      // @ts-ignore - Using a simpler configuration to avoid TypeScript issues
      await paddle.Checkout.open({
        settings: {
          displayMode: "overlay",
          theme: "light",
          successUrl: window.location.origin + "/pricing?upgraded=true",
        },
        items: [
          {
            priceId: import.meta.env.VITE_PADDLE_PREMIUM_PRICE_ID || "",
            quantity: 1,
          },
        ],
        customer: {
          email: user.email,
        },
        customData: {
          email: user.email,
        },
      });

      // No mixpanel tracking as requested by user
    } catch (error) {
      console.error("Checkout error:", error);

      // No mixpanel tracking as requested by user

      throw error; // Re-throw the error to be handled by the caller
    }
  };

  return {
    ...status,
    startCheckout,
  };
}
